<script setup>
// Page 6 — what you actually see when a month doesn't go to plan: a deck of cards you
// scroll THROUGH, one at a time, before the page releases to the next. An inner sticky
// wrapper holds the deck fixed in the viewport while a ScrollTrigger maps the section's
// scroll progress onto a continuous "active card" position. The front card lifts away
// and fades as the next rises into place — a physical shuffle driven purely by how far
// you've scrolled. Using CSS `sticky` (not ScrollTrigger pin) keeps it friendly with
// the page-level scroll-snap.
//
// THREE cards, not five, and each one is an operational answer rather than a feature.
// The five it replaced included "Nette btw-facturen" (which is Pandig's own invoice to
// the VvE, not something a buyer is choosing between tools for) and two that restated
// the money-flow page. Card count drives the section's height through the --cards
// custom property, so dropping two cards took this page from 490vh to ~165vh on
// desktop — it was the single largest source of scroll fatigue on the site, and the
// shuffle reads better at three anyway: you can hold three things in your head.
//
// Civic language: square cards, 2px ink borders, the hard-lg shadow on the live card.
//
// Deliberately not `.snap-page`: it's several viewports tall, and giving an
// oversized box its own scroll-snap-align + stop:always point is what was trapping
// the mouse wheel here (see application.css). It's framed by real `.snap-page`
// panels on both sides, so it just scrolls through freely in between them.
import { ref, onMounted, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import { Eye, AlertTriangle, ShieldCheck, MousePointer2 } from "@lucide/vue";
import { gsap, ScrollTrigger, useMotion, reducedMotion, SCRUB } from "@/lib/motion";

const { t } = useI18n();

const cards = [
  { id: "foresight", icon: Eye },
  { id: "failure", icon: AlertTriangle },
  { id: "calm", icon: ShieldCheck },
];

const root = ref(null);
const cardEls = ref([]);
const active = ref(0);
const progress = ref(0);

let cleanup = () => {};

// Place one card given its position relative to the continuously-interpolated active
// index: >=0 means it's the live card (0) or still stacked behind; <0 means it has
// been scrolled past and is flying up and out.
function place(el, rel, i) {
  let y, scale, opacity, rot, z;
  if (rel >= 0) {
    const d = rel;
    y = -d * 20;
    scale = Math.max(0.78, 1 - d * 0.06);
    opacity = Math.max(0, 1 - d * 0.24);
    rot = (i % 2 ? 1 : -1) * d * 1.6;
    z = 100 - Math.round(d);
  } else {
    const p = Math.min(1.3, -rel);
    y = -100 - p * 90;
    scale = 1 - p * 0.06;
    opacity = Math.max(0, 1 - p * 1.5);
    rot = (i % 2 ? 1 : -1) * (6 + p * 6);
    z = 200 + i;
  }
  gsap.set(el, { y, scale, opacity, rotate: rot, zIndex: z });
}

function render(p) {
  progress.value = p;
  const a = p * (cards.length - 1);
  active.value = Math.min(cards.length - 1, Math.round(a));
  cards.forEach((c, i) => {
    const el = cardEls.value[i];
    if (el) place(el, i - a, i);
  });
}

onMounted(() => {
  // Unlike the scrub itself, the section's scroll length and the card
  // shuffle aren't something reduced-motion can settle into a resting state — they
  // only exist as scroll-linked motion. Reduced-motion visitors get a plain static
  // grid instead (see template), so there's nothing here to wire up for them.
  if (reducedMotion) return;

  cleanup = useMotion(root.value, () => {
    render(0);
    ScrollTrigger.create({
      trigger: root.value,
      start: "top top",
      end: "bottom bottom",
      scrub: SCRUB,
      onUpdate: (self) => render(self.progress),
    });
  });
});

onUnmounted(() => cleanup());
</script>

<template>
  <!-- The deck's scroll length is one card's worth of scroll per card, and a phone gets
       a shorter allowance per card: a viewport is a much bigger share of a touch flick
       than of a wheel tick, so the desktop figure reads as endless there. The card
       count drives it through a custom property so the two heights stay derived from
       `cards` rather than hard-coded alongside it — which is what made dropping from
       five cards to three shorten this page automatically. -->
  <section
    id="wat-je-ziet"
    ref="root"
    class="relative border-t-2 border-foreground bg-card"
    :class="reducedMotion ? '' : 'h-[calc(var(--cards)*38vh_+_25vh)] sm:h-[calc(var(--cards)*42vh_+_28vh)]'"
    :data-page-label="t('landing.pager.proof')"
    :style="reducedMotion ? {} : { '--cards': cards.length }"
  >
    <!-- Reduced motion: the same three cards, laid out plainly instead of scrolled through. -->
    <div v-if="reducedMotion" class="page-panel mx-auto flex w-full max-w-6xl flex-col justify-center px-5 py-16">
      <h2 class="font-sans text-3xl font-bold tracking-tight text-foreground sm:text-4xl">{{ t("landing.proof.title") }}</h2>
      <p class="mt-4 max-w-md text-base leading-relaxed text-muted-foreground">{{ t("landing.proof.lead") }}</p>

      <div class="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        <article
          v-for="c in cards"
          :key="c.id"
          class="flex flex-col gap-4 border-2 border-foreground bg-background p-6 shadow-[var(--shadow-hard)]"
        >
          <span class="flex size-11 items-center justify-center border-2 border-foreground bg-secondary text-primary">
            <component :is="c.icon" class="size-5" />
          </span>
          <div>
            <h3 class="font-sans text-lg font-bold tracking-tight text-foreground">{{ t(`landing.proof.${c.id}_title`) }}</h3>
            <p class="mt-2 text-sm leading-relaxed text-muted-foreground">{{ t(`landing.proof.${c.id}_body`) }}</p>
          </div>
        </article>
      </div>
    </div>

    <div v-else class="page-camera flex [align-items:safe_center] overflow-y-auto lg:items-center lg:overflow-hidden">
      <div class="mx-auto grid w-full max-w-6xl items-center gap-4 px-5 py-5 sm:gap-6 sm:py-10 lg:grid-cols-[1fr_1.1fr] lg:gap-14 lg:py-16">
        <div>
          <h2 class="font-sans text-xl font-bold leading-tight tracking-tight text-foreground sm:text-3xl">{{ t("landing.proof.title") }}</h2>
          <p class="mt-2.5 max-w-md text-sm leading-snug text-muted-foreground sm:mt-4 sm:text-base sm:leading-relaxed">{{ t("landing.proof.lead") }}</p>

          <!-- Scroll-through progress: a ticking counter + a segmented bar. -->
          <div class="mt-5 flex items-center gap-3 sm:mt-8 sm:gap-4">
            <span class="font-mono text-3xl font-bold tabular-nums text-primary sm:text-4xl">{{ String(active + 1).padStart(2, "0") }}</span>
            <div class="flex flex-1 gap-1.5">
              <span
                v-for="(c, i) in cards"
                :key="c.id"
                class="h-1.5 flex-1 border border-foreground transition-colors duration-200"
                :class="i <= active ? 'bg-primary' : 'bg-transparent'"
              />
            </div>
            <span class="font-mono text-xs font-semibold tabular-nums text-muted-foreground">{{ String(cards.length).padStart(2, "0") }}</span>
          </div>
          <p
            class="mt-3 inline-flex items-center gap-1.5 font-mono text-[0.625rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground transition-opacity duration-300 sm:mt-4"
            :style="{ opacity: progress > 0.02 ? 0 : 1 }"
          >
            <MousePointer2 class="size-3.5" />{{ t("landing.proof.hint") }}
          </p>
        </div>

        <!-- The deck: absolutely-positioned cards driven by scroll. -->
        <div class="relative mx-auto h-56 w-full max-w-md sm:h-80">
          <article
            v-for="(c, i) in cards"
            :key="c.id"
            :ref="(el) => (cardEls[i] = el)"
            class="absolute inset-x-0 top-3 flex h-52 flex-col justify-between border-2 border-foreground bg-background p-4 shadow-[var(--shadow-hard-lg)] sm:top-4 sm:h-64 sm:p-7"
          >
            <span class="flex size-9 items-center justify-center border-2 border-foreground bg-secondary text-primary sm:size-11">
              <component :is="c.icon" class="size-4 sm:size-5" />
            </span>
            <div>
              <h3 class="font-sans text-base font-bold tracking-tight text-foreground sm:text-xl">{{ t(`landing.proof.${c.id}_title`) }}</h3>
              <p class="mt-1.5 text-xs leading-snug text-muted-foreground sm:mt-2 sm:text-sm sm:leading-relaxed">{{ t(`landing.proof.${c.id}_body`) }}</p>
            </div>
          </article>
        </div>
      </div>
    </div>
  </section>
</template>
